import React, { useState } from "react";
import { Car, MapPin, Calendar, ArrowLeftRight, Users, Snowflake, Briefcase, ArrowRight, CheckCircle2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import { CAB_TYPES } from "../mock/mock";
import { useToast } from "../hooks/use-toast";

const CabSearch = () => {
  const { toast } = useToast();
  const today = new Date().toISOString().slice(0, 10);

  const [q, setQ] = useState({ from: "", to: "", date: today, trip: "oneway" });
  const [selected, setSelected] = useState(null);
  const [pax, setPax] = useState({ name: "", phone: "", pickup: "" });
  const [confirmed, setConfirmed] = useState(null);

  const swap = () => setQ({ ...q, from: q.to, to: q.from });

  const openBooking = (data) => {
    if (!q.from || !q.to) {
      toast({ title: "Add pickup & drop cities", description: "Fill journey details before booking." });
      return;
    }
    setSelected(data);
  };

  const closeDialog = () => {
    setSelected(null);
    setConfirmed(null);
    setPax({ name: "", phone: "", pickup: "" });
  };

  const confirmBooking = (e) => {
    e.preventDefault();
    if (!pax.name || !pax.phone) {
      toast({ title: "Details required", description: "Please enter name & phone." });
      return;
    }
    setConfirmed({ code: "PMRC" + Date.now().toString().slice(-8), data: selected });
  };

  return (
    <section id="cabs" className="py-20 md:py-28 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-100 text-teal-700 text-xs font-semibold tracking-wider uppercase">
            <Car className="w-3.5 h-3.5" /> Car / Cab Booking
          </div>
          <h2 className="mt-4 text-3xl md:text-5xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "'Poppins', sans-serif" }}>
            Book a private car in a click
          </h2>
          <p className="mt-4 text-slate-600 text-lg">
            One-way, round-trip, hourly or airport transfer — all-inclusive fares with driver, fuel and tolls.
          </p>
        </div>

        <div className="mt-10 bg-white rounded-2xl shadow-xl shadow-slate-900/5 border border-slate-100 p-4">
          <div className="flex flex-wrap gap-2 mb-3">
            {[
              { id: "oneway", label: "One Way" },
              { id: "round", label: "Round Trip" },
              { id: "local", label: "Local (Hourly)" },
              { id: "airport", label: "Airport" },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setQ({ ...q, trip: t.id })}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition ${
                  q.trip === t.id
                    ? "bg-teal-600 text-white border-teal-600"
                    : "bg-white text-slate-600 border-slate-200 hover:border-teal-300"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_1fr] gap-2 items-center">
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-50">
              <MapPin className="w-5 h-5 text-teal-600" />
              <div className="flex-1">
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">Pickup</label>
                <Input
                  value={q.from}
                  onChange={(e) => setQ({ ...q, from: e.target.value })}
                  placeholder="Churu"
                  className="border-0 p-0 h-6 focus-visible:ring-0 shadow-none text-slate-900 placeholder:text-slate-400"
                />
              </div>
            </div>
            <button
              type="button"
              onClick={swap}
              className="hidden md:flex w-10 h-10 rounded-full bg-slate-100 hover:bg-teal-600 hover:text-white items-center justify-center text-slate-600 transition"
              aria-label="swap"
            >
              <ArrowLeftRight className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-50 border-t md:border-t-0 md:border-l border-slate-100">
              <MapPin className="w-5 h-5 text-orange-500" />
              <div className="flex-1">
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">Drop</label>
                <Input
                  value={q.to}
                  onChange={(e) => setQ({ ...q, to: e.target.value })}
                  placeholder="Jaipur"
                  className="border-0 p-0 h-6 focus-visible:ring-0 shadow-none text-slate-900 placeholder:text-slate-400"
                />
              </div>
            </div>
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-50 border-t md:border-t-0 md:border-l border-slate-100">
              <Calendar className="w-5 h-5 text-teal-600" />
              <div className="flex-1">
                <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">Pickup Date</label>
                <Input
                  type="date"
                  value={q.date}
                  onChange={(e) => setQ({ ...q, date: e.target.value })}
                  className="border-0 p-0 h-6 focus-visible:ring-0 shadow-none text-slate-900"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10">
          <div className="flex items-end justify-between mb-4">
            <h3 className="text-xl font-bold text-slate-900">Choose Your Car</h3>
            <span className="text-sm text-slate-500">Includes driver, fuel & tolls</span>
          </div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {CAB_TYPES.map((c) => (
              <div
                key={c.id}
                className="group bg-white rounded-2xl overflow-hidden border border-slate-200 hover:border-teal-500 hover:shadow-xl hover:-translate-y-0.5 transition"
              >
                <div className="relative h-40 overflow-hidden bg-slate-100">
                  <img src={c.image} alt={c.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                  <Badge className="absolute top-3 left-3 bg-white/95 text-slate-800 hover:bg-white">{c.type}</Badge>
                </div>
                <div className="p-5">
                  <div className="flex items-start justify-between">
                    <h4 className="font-bold text-slate-900 text-lg">{c.name}</h4>
                    <div className="text-right">
                      <div className="text-[10px] text-slate-500 uppercase">from</div>
                      <div className="text-lg font-bold text-teal-700">₹{c.base.toLocaleString("en-IN")}</div>
                    </div>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2 text-[11px] text-slate-600">
                    <span className="flex items-center gap-1"><Users className="w-3 h-3 text-teal-600" /> {c.seats}</span>
                    <span className="flex items-center gap-1"><Snowflake className="w-3 h-3 text-teal-600" /> A/C</span>
                    <span className="flex items-center gap-1"><Briefcase className="w-3 h-3 text-teal-600" /> {c.luggage}</span>
                  </div>
                  <div className="mt-3 text-[11px] text-slate-500">
                    Additional ₹{c.rate}/km after included distance
                  </div>
                  <Button
                    onClick={() => openBooking(c)}
                    className="mt-4 w-full bg-teal-600 hover:bg-teal-700"
                  >
                    Book This Car <ArrowRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <Dialog open={!!selected} onOpenChange={(v) => !v && closeDialog()}>
        <DialogContent className="max-w-lg">
          {selected && !confirmed && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl" style={{ fontFamily: "'Poppins', sans-serif" }}>
                  Book Car
                </DialogTitle>
                <DialogDescription>
                  {selected.name} ({selected.type}) · {q.from} → {q.to}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={confirmBooking} className="mt-4 space-y-4">
                <div>
                  <Label htmlFor="cname" className="text-xs">Full Name *</Label>
                  <Input id="cname" value={pax.name} onChange={(e) => setPax({ ...pax, name: e.target.value })} placeholder="Ravi Kumar" />
                </div>
                <div>
                  <Label htmlFor="cphone" className="text-xs">Phone *</Label>
                  <Input id="cphone" value={pax.phone} onChange={(e) => setPax({ ...pax, phone: e.target.value })} placeholder="+91 98765 43210" />
                </div>
                <div>
                  <Label htmlFor="cpickup" className="text-xs">Pickup Address (optional)</Label>
                  <Input id="cpickup" value={pax.pickup} onChange={(e) => setPax({ ...pax, pickup: e.target.value })} placeholder="Full address for pickup" />
                </div>
                <div className="pt-3 border-t border-slate-100 flex items-end justify-between">
                  <div>
                    <div className="text-xs text-slate-500">Total (approx.)</div>
                    <div className="text-2xl font-bold text-slate-900">₹{selected.base.toLocaleString("en-IN")}</div>
                  </div>
                  <Button type="submit" className="bg-teal-600 hover:bg-teal-700">Confirm Booking</Button>
                </div>
              </form>
            </>
          )}

          {confirmed && (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full bg-green-100 mx-auto flex items-center justify-center">
                <CheckCircle2 className="w-9 h-9 text-green-600" />
              </div>
              <h3 className="mt-5 text-2xl font-bold text-slate-900" style={{ fontFamily: "'Poppins', sans-serif" }}>
                Car Booked!
              </h3>
              <p className="mt-2 text-slate-600 text-sm">
                A confirmation SMS has been sent to your phone. Driver details will be shared 2 hours before pickup.
              </p>
              <div className="mt-5 rounded-lg bg-slate-50 border border-slate-200 p-4 text-sm text-left space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">Booking ID</span><span className="font-mono font-semibold">{confirmed.code}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Car</span><span className="font-semibold">{confirmed.data.name}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Passenger</span><span className="font-semibold">{pax.name}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Phone</span><span className="font-semibold">{pax.phone}</span></div>
              </div>
              <Button onClick={closeDialog} className="mt-6 bg-teal-600 hover:bg-teal-700">Done</Button>
              <p className="mt-4 text-[11px] text-orange-600 font-semibold">DEMO MODE — Booking is MOCKED (no real transaction).</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default CabSearch;
